import React, { useState } from 'react';
import { X, LayoutDashboard, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createDashboard, dashboardsApi } from '../../api/dashboardsApi';
import { useDashboardFolders } from '../../../analytics-shared/hooks/useFolders';
import { VISIBILITY_OPTIONS } from '../../../analytics-shared/constants';

const CreateDashboardModal = ({ open, onClose, onCreated }) => {
  const navigate = useNavigate();
  const { folders } = useDashboardFolders();
  const [name, setName] = useState('');
  const [folder, setFolder] = useState('Unfiled');
  const [visibility, setVisibility] = useState('Private');
  const [error, setError] = useState('');

  const reset = () => {
    setName(''); setFolder('Unfiled'); setVisibility('Private'); setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Dashboard name is required'); return; }
    const dash = createDashboard({ name: name.trim(), folder, visibility, createdBy: 'You' });
    const saved = await dashboardsApi.save(dash);
    onCreated?.(saved);
    reset();
    onClose();
    navigate(`/reports/dashboards/${saved.id}/edit`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <LayoutDashboard className="w-4 h-4 text-indigo-600" />
            </div>
            <h2 className="text-base font-bold text-slate-800">New Dashboard</h2>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dashboard Name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Q2 Sales Overview"
              className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors ${
                error ? 'border-red-400 bg-red-50' : 'border-slate-200'
              }`}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Folder</label>
              <select value={folder} onChange={(e) => setFolder(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                <option value="Unfiled">Unfiled</option>
                {folders.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Visibility</label>
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                {VISIBILITY_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <button onClick={handleClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            Cancel
          </button>
          <button onClick={handleCreate}
            className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-sm">
            Create &amp; Edit
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateDashboardModal;
